package one.healthium.onboarding.domain.models

import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.encoding.decodeStructure
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import one.healthium.domain.user.models.Gender

@Serializable(with = GenderTextSerializers::class)
data class GenderText(
    @SerialName("men") val men: String,
    @SerialName("women") val women: String,
) {
    constructor(text: String) : this(text, text)

    fun value(gender: Gender?): String {
        return when (gender) {
            Gender.Female -> women
            Gender.Male -> men
            else -> women
        }
    }
}

object GenderTextSerializers : KSerializer<GenderText> {
    override val descriptor: SerialDescriptor = buildClassSerialDescriptor("one.healthium.domain.common.GenderText") {}

    override fun deserialize(decoder: Decoder): GenderText {
        val input = decoder as JsonDecoder

        return when (val element = input.decodeJsonElement()) {
            is JsonObject -> input.json.decodeFromJsonElement(ObjectSerializer, element)
            is JsonPrimitive -> GenderText(input.json.decodeFromJsonElement(String.serializer(), element))
            else -> error("Unexpected JSON element")
        }
    }

    override fun serialize(encoder: Encoder, value: GenderText) {
        ObjectSerializer.serialize(encoder, value)
    }
}

private object ObjectSerializer : KSerializer<GenderText> {
    override val descriptor: SerialDescriptor = buildClassSerialDescriptor("one.healthium.domain.common.GenderText") {
        element("men", PrimitiveSerialDescriptor("String", PrimitiveKind.STRING))
        element("women", PrimitiveSerialDescriptor("String", PrimitiveKind.STRING))
    }

    override fun deserialize(decoder: Decoder): GenderText {
        return decoder.decodeStructure(descriptor) {
            var men: String? = null
            var women: String? = null
            while (true) {
                when (val index = decodeElementIndex(descriptor)) {
                    0 -> men = decodeStringElement(descriptor, 0)
                    1 -> women = decodeStringElement(descriptor, 1)
                    CompositeDecoder.DECODE_DONE -> break
                    else -> error("Unexpected index: $index")
                }
            }
            if (men == null || women == null) throw IllegalArgumentException("GenderText must have two values")

            GenderText(
                men = men,
                women = women,
            )
        }
    }

    override fun serialize(encoder: Encoder, value: GenderText) {
        encoder.beginStructure(descriptor).apply {
            encodeStringElement(descriptor, 0, value.men)
            encodeStringElement(descriptor, 1, value.women)
            endStructure(descriptor)
        }
    }
}